// src/pages/builder/LessonPictureTools.js
//
// Picture tools for the Lesson video studio's storyboard (LessonVideoPanel):
//
//   PictureLook     the look for the WHOLE video, picked with buttons —
//                   art style (the fish chart), background, no people,
//                   "same drawing in every picture" — plus the song-wide
//                   directions box.
//   GroupsBar       colour groups. Lines painted the same colour share ONE
//                   picture: make it once, it goes on every line of that
//                   colour (a repeated chorus, "same as above").
//   StorylineBox    the whole storyline as text, the way it was written in
//                   chat:  lyric line (what the picture shows)
//                   Same notes → same colour; "same as above" → same colour
//                   as the earlier line with the same words; "zoom in to the
//                   side / under the bars" → a camera move on that line.
//
// Nothing here writes to Firestore. Everything edits the storyboard in
// memory; LessonVideoPanel's Save storyboard saves it.

import React, { useMemo, useState } from "react";
import { formatTime } from "../../services/lyricSyncService";
import {
  ART_STYLES,
  BACKGROUNDS,
  GROUP_COLORS,
  groupColor,
} from "../../components/video/artStyles";

/* ------------------------------------------------------------------ */
/* Shared helpers                                                      */
/* ------------------------------------------------------------------ */

export const normText = (t) =>
  String(t || "")
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const groupIdOf = (sb, i) => sb?.lineGroups?.[String(i)] || "";

/** Lines (from sung) in a group, in song order. */
export const linesInGroup = (sb, sung, gid) => sung.filter((ln) => groupIdOf(sb, ln.i) === gid);

/** A group id that isn't taken yet. */
const newGroupId = (groups) => {
  let n = Object.keys(groups || {}).length + 1;
  while ((groups || {})[`g${n}`]) n += 1;
  return `g${n}`;
};

/**
 * The first colour not used by a group yet (wraps round after 12). If the
 * note names a colour ("blue bar the tallest"), that colour is picked when
 * it's free, so the dots read like the pictures.
 */
const nextColor = (groups, note = "") => {
  const used = new Set(Object.values(groups || {}).map((g) => g.color));
  const named = String(note || "")
    .toLowerCase()
    .match(/\b(red|blue|green|yellow|purple|orange|pink|teal|brown|navy|lime|grey|gray)\b/);
  const want = named ? (named[1] === "gray" ? "grey" : named[1]) : "";
  if (want && !used.has(want)) return want;
  return (GROUP_COLORS.find((c) => !used.has(c.key)) || GROUP_COLORS[Object.keys(groups || {}).length % GROUP_COLORS.length]).key;
};

/**
 * Puts line i in group gid ("" = no group). If the group already has a
 * picture, the line switches to it straight away. Returns the new storyboard.
 */
export const assignLineToGroup = (sb, i, gid) => {
  const lineGroups = { ...(sb.lineGroups || {}) };
  const byLine = { ...(sb.byLine || {}) };
  if (!gid) {
    delete lineGroups[String(i)];
    return { ...sb, lineGroups };
  }
  lineGroups[String(i)] = gid;
  const url = sb.groups?.[gid]?.url || "";
  if (url) {
    const cur = byLine[i] ?? byLine[String(i)];
    const base = cur && !cur.keep && cur.scene === "picture" ? cur : { cast: cur?.cast || {} };
    byLine[i] = { ...base, scene: "picture", picture: { ...(base.picture || {}), url } };
  }
  return { ...sb, lineGroups, byLine };
};

/** Camera move written in words in a storyline note, or null. */
export const cameraFromNote = (note) => {
  const n = String(note || "").toLowerCase();
  if (!/zoom|close[- ]?up|pan /.test(n)) return null;
  if (/zoom(s|ing)? (back )?out|back out|full graph|whole graph/.test(n)) return "";
  if (/\b(side|scale|left|axis)\b/.test(n)) return "zoomLeft";
  if (/\b(under|bottom|below|names?|labels?|values?)\b/.test(n)) return "zoomBottom";
  if (/\bright\b/.test(n)) return "zoomRight";
  if (/\b(top|above)\b/.test(n)) return "zoomTop";
  if (/pan /.test(n)) return "panRight";
  return "zoomCenter";
};

/* ------------------------------------------------------------------ */
/* Picture look (whole video)                                          */
/* ------------------------------------------------------------------ */

export const PictureLook = ({ sb, setSb }) => {
  const [open, setOpen] = useState(true);
  const set = (patch) => setSb({ ...sb, ...patch });
  const style = ART_STYLES.find((s) => s.key === sb.artStyle);
  const bg = BACKGROUNDS.find((b) => b.key === (sb.background || "")) || BACKGROUNDS[0];
  return (
    <div className="lv-look">
      <button type="button" className="lv-look-head" onClick={() => setOpen((v) => !v)}>
        <span>{open ? "▾" : "▸"}</span>
        <strong>🎨 Picture look for the whole video</strong>
        <span className="sa-muted">
          {style ? style.label : "SBL cartoon (default)"} · background: {bg.short}
          {sb.noPeople ? " · no people" : ""}
          {sb.sameDrawing ? " · same drawing every time" : ""}
        </span>
      </button>

      {open && (
        <div className="lv-look-body">
          <div className="lv-look-label">
            Style <span className="sa-muted">— used for every picture in this video</span>
          </div>
          <div className="lv-styles" role="radiogroup" aria-label="Picture style">
            <button
              type="button"
              role="radio"
              aria-checked={!sb.artStyle}
              className={`lv-style ${!sb.artStyle ? "is-on" : ""}`}
              onClick={() => set({ artStyle: "" })}
              title="The Image studio's SBL illustration style"
            >
              <span className="lv-style-sbl">SBL</span>
              <span className="lv-style-name">SBL default</span>
            </button>
            {ART_STYLES.map((s) => (
              <button
                type="button"
                role="radio"
                aria-checked={sb.artStyle === s.key}
                key={s.key}
                className={`lv-style ${sb.artStyle === s.key ? "is-on" : ""}`}
                onClick={() => set({ artStyle: s.key })}
                title={s.prompt}
              >
                <img src={s.img} alt="" />
                <span className="lv-style-name">{s.label}</span>
              </button>
            ))}
          </div>

          <div className="lv-look-label">Background</div>
          <div className="lv-seg" role="radiogroup" aria-label="Background">
            {BACKGROUNDS.map((b) => (
              <button
                type="button"
                role="radio"
                aria-checked={(sb.background || "") === b.key}
                key={b.key || "auto"}
                className={`lv-seg-btn ${(sb.background || "") === b.key ? "is-on" : ""}`}
                onClick={() => set({ background: b.key })}
              >
                {b.key === "plain" ? "⬜ " : b.key === "color" ? "🟦 " : b.key === "scene" ? "🏞 " : ""}
                {b.label}
              </button>
            ))}
          </div>

          <div className="lv-look-toggles">
            <label className="sa-check lv-check">
              <input type="checkbox" checked={Boolean(sb.noPeople)} onChange={(e) => set({ noPeople: e.target.checked })} />
              No people or characters in the pictures
            </label>
            <label className="sa-check lv-check">
              <input
                type="checkbox"
                checked={Boolean(sb.sameDrawing)}
                onChange={(e) => set({ sameDrawing: e.target.checked })}
              />
              Same drawing in every picture — the first picture is redrawn each time, changing only what each line or
              colour says (e.g. the same bar graph with different bars tallest)
            </label>
          </div>

          <label className="lv-song-dir">
            <span>
              <strong>What every picture shows</strong>{" "}
              <span className="sa-muted">Sent with every AI picture for this song. Saved with the storyboard.</span>
            </span>
            <textarea
              rows={2}
              value={sb.pictureStyle || ""}
              placeholder="e.g. A colourful bar graph with 4 bars, always in this order left to right: red, green, blue, yellow. Axes, a number scale on the left (0–10) and a label under each bar."
              onChange={(e) => set({ pictureStyle: e.target.value })}
            />
          </label>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Colour groups                                                       */
/* ------------------------------------------------------------------ */

/**
 * brush: the group id being painted with ("" = rubber, null = not painting).
 * timesOf(i) → [seconds…] every time line i is sung.
 */
export const GroupsBar = ({
  sb,
  setSb,
  sung,
  timesOf,
  brush,
  setBrush,
  onMake,
  busyGroup,
  promptOf,
}) => {
  const groups = sb.groups || {};
  const ids = useMemo(() => {
    // in the order they're first used in the song, unused ones last
    const order = [];
    sung.forEach((ln) => {
      const g = groupIdOf(sb, ln.i);
      if (g && groups[g] && !order.includes(g)) order.push(g);
    });
    Object.keys(groups).forEach((g) => !order.includes(g) && order.push(g));
    return order;
  }, [sb, sung, groups]);
  const [showPrompt, setShowPrompt] = useState(null);
  const [errors, setErrors] = useState({});

  const addGroup = () => {
    const id = newGroupId(groups);
    const color = nextColor(groups);
    setSb({ ...sb, groups: { ...groups, [id]: { color, name: groupColor(color).name, idea: "" } } });
    setBrush(id);
  };
  const setGroup = (id, patch) => setSb({ ...sb, groups: { ...groups, [id]: { ...groups[id], ...patch } } });
  const removeGroup = (id) => {
    const n = linesInGroup(sb, sung, id).length;
    if (n && !window.confirm(`Remove the ${groups[id]?.name || "this"} colour? Its ${n} line${n === 1 ? "" : "s"} keep the picture they have now.`)) return;
    const next = { ...groups };
    delete next[id];
    const lineGroups = { ...(sb.lineGroups || {}) };
    Object.keys(lineGroups).forEach((k) => lineGroups[k] === id && delete lineGroups[k]);
    setSb({ ...sb, groups: next, lineGroups });
    if (brush === id) setBrush(null);
  };
  const make = async (id, quality) => {
    setErrors((e) => ({ ...e, [id]: "" }));
    try {
      await onMake(id, quality);
    } catch (err) {
      setErrors((e) => ({ ...e, [id]: err?.message || String(err) }));
    }
  };

  return (
    <div className="lv-groups">
      <div className="lv-groups-head">
        <strong>🖍 Picture colours</strong>
        <span className="sa-muted">
          Lines with the same colour share one picture. Pick a colour, then click the dot ● on each line to paint it.
          Click a painted dot again to take the colour off.
        </span>
      </div>

      <div className="lv-brushes">
        {ids.map((id) => {
          const g = groups[id];
          const c = groupColor(g.color);
          return (
            <button
              type="button"
              key={id}
              className={`lv-brush ${brush === id ? "is-on" : ""}`}
              style={{ "--g": c.hex }}
              onClick={() => setBrush(brush === id ? null : id)}
              title={brush === id ? "Painting with this colour — click again to stop" : "Paint lines with this colour"}
            >
              <span className="lv-dot" />
              {g.name || c.name}
              <span className="lv-brush-n">{linesInGroup(sb, sung, id).length}</span>
            </button>
          );
        })}
        <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={addGroup}>
          + New colour
        </button>
        {brush !== null && (
          <button
            type="button"
            className={`lv-brush lv-brush-rubber ${brush === "" ? "is-on" : ""}`}
            onClick={() => setBrush(brush === "" ? null : "")}
            title="Click a line's dot to take its colour off"
          >
            ⌫ Rubber
          </button>
        )}
        {brush !== null && (
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setBrush(null)}>
            Done painting
          </button>
        )}
      </div>

      {ids.length > 0 && (
        <div className="lv-group-cards">
          {ids.map((id) => {
            const g = groups[id];
            const c = groupColor(g.color);
            const members = linesInGroup(sb, sung, id);
            const times = members.flatMap((ln) => timesOf(ln.i)).sort((a, b) => a - b);
            const busy = busyGroup === id;
            return (
              <div key={id} className={`lv-group ${brush === id ? "is-on" : ""}`} style={{ "--g": c.hex }}>
                <div className="lv-group-top">
                  <select
                    className="lv-group-color"
                    value={g.color}
                    onChange={(e) => setGroup(id, { color: e.target.value })}
                    aria-label="Colour"
                  >
                    {GROUP_COLORS.map((col) => (
                      <option key={col.key} value={col.key}>
                        ● {col.name}
                      </option>
                    ))}
                  </select>
                  <input
                    className="lv-group-name"
                    value={g.name || ""}
                    placeholder={c.name}
                    onChange={(e) => setGroup(id, { name: e.target.value })}
                    aria-label="Name"
                  />
                  {g.url ? (
                    <img className="lv-group-thumb" src={g.url} alt="" loading="lazy" />
                  ) : (
                    <span className="sa-pill-inline tone-amber">no picture</span>
                  )}
                  <button type="button" className="sa-x lv-group-x" onClick={() => removeGroup(id)} aria-label="Remove colour">
                    ✕
                  </button>
                </div>
                <textarea
                  rows={2}
                  value={g.idea || ""}
                  disabled={busy}
                  placeholder="What this picture shows, e.g. BLUE is the tallest bar; red, green and yellow are shorter."
                  onChange={(e) => setGroup(id, { idea: e.target.value })}
                />
                <div className="lv-group-meta sa-muted">
                  {members.length === 0
                    ? "No lines yet — pick this colour and click the dots on the lines."
                    : `${members.length} line${members.length === 1 ? "" : "s"}` +
                      (times.length ? ` · on screen at ${times.slice(0, 8).map((t) => formatTime(t, 0)).join(", ")}${times.length > 8 ? " …" : ""}` : "")}
                </div>
                <div className="lv-group-actions">
                  <button
                    type="button"
                    className="lb-btn lb-btn-sm"
                    disabled={busy || !members.length || busyGroup === "*"}
                    onClick={() => make(id)}
                  >
                    {busy ? "Making…" : g.url ? "✨ Make it again" : "✨ Make picture"}
                  </button>
                  <button
                    type="button"
                    className="lb-btn lb-btn-ghost lb-btn-sm"
                    disabled={!members.length}
                    onClick={() => setShowPrompt(showPrompt === id ? null : id)}
                  >
                    {showPrompt === id ? "Hide prompt" : "👁 See the prompt"}
                  </button>
                  {busy && <span className="sa-muted">10–40 seconds</span>}
                </div>
                {showPrompt === id && members.length > 0 && <pre className="lv-prompt">{promptOf(id)}</pre>}
                {errors[id] && <div className="sa-banner tone-red">{errors[id]}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** The ● on a storyboard row. Paints with the brush, or shows the colour. */
export const LineDot = ({ sb, i, brush, onPaint }) => {
  const gid = groupIdOf(sb, i);
  const g = gid ? sb.groups?.[gid] : null;
  const c = g ? groupColor(g.color) : null;
  const painting = brush !== null && brush !== undefined;
  return (
    <button
      type="button"
      className={`lv-linedot ${g ? "is-set" : ""} ${painting ? "is-painting" : ""}`}
      style={c ? { "--g": c.hex } : undefined}
      title={
        painting
          ? brush === ""
            ? "Take the colour off this line"
            : g && gid === brush
            ? "Click to take this colour off"
            : `Paint this line ${sb.groups?.[brush]?.name || ""}`
          : g
          ? `${g.name || c.name} — shares a picture with the other ${c.name.toLowerCase()} lines`
          : "Pick a colour above, then click here"
      }
      onClick={(e) => {
        e.stopPropagation();
        if (!painting) return;
        onPaint(i, brush === "" || gid === brush ? "" : brush);
      }}
    />
  );
};

/* ------------------------------------------------------------------ */
/* The storyline as text                                               */
/* ------------------------------------------------------------------ */

/** "lyric (note)" → { lyric, note }. The note may hold (brackets) itself. */
export const splitNote = (raw) => {
  const s = String(raw || "").trim();
  if (!s.endsWith(")")) return { lyric: s, note: "" };
  let depth = 0;
  for (let k = s.length - 1; k >= 0; k -= 1) {
    if (s[k] === ")") depth += 1;
    else if (s[k] === "(") {
      depth -= 1;
      if (depth === 0) return { lyric: s.slice(0, k).trim(), note: s.slice(k + 1, -1).trim() };
    }
  }
  return { lyric: s, note: "" };
};

const SAME_RE = /^\s*(same( as (above|before|the (first|last) time|earlier))?|repeat|ditto|again)\b/i;

/** The storyboard written out as text: each line, then (its directions). */
export const storylineText = (sb, sung) => {
  const out = [];
  let section = null;
  sung.forEach((ln) => {
    if (ln.section !== section) {
      if (out.length) out.push("");
      if (ln.section) out.push(`(${ln.section})`);
      section = ln.section;
    }
    const gid = groupIdOf(sb, ln.i);
    const shot = sb.byLine?.[ln.i] ?? sb.byLine?.[String(ln.i)];
    const idea = gid ? sb.groups?.[gid]?.idea || "" : shot?.picture?.idea || "";
    out.push(idea.trim() ? `${ln.text} (${idea.trim().replace(/\s+/g, " ")})` : ln.text);
  });
  return out.join("\n");
};

const words = (t) => new Set(normText(t).split(" ").filter(Boolean));
const similar = (a, b) => {
  const A = words(a);
  const B = words(b);
  if (!A.size || !B.size) return 0;
  let n = 0;
  A.forEach((w) => B.has(w) && (n += 1));
  return n / Math.max(A.size, B.size);
};

/**
 * Reads a storyline and returns the new storyboard plus what happened.
 *   lyric line (picture directions)
 * - identical directions → the same colour (one picture)
 * - "same as above" → the colour of the earlier line with the same words
 * - "zoom in to the side…" → camera move on that line (picture kept)
 * Lines without (directions) are left as they are.
 */
export const applyStoryline = (sb, sung, text) => {
  const groups = { ...(sb.groups || {}) };
  let next = { ...sb, groups, lineGroups: { ...(sb.lineGroups || {}) }, byLine: { ...(sb.byLine || {}) } };
  const byKey = {};
  Object.entries(groups).forEach(([id, g]) => {
    if (g.idea) byKey[normText(g.idea)] = id;
  });
  const lastGroupForText = {};
  let ptr = 0;
  const report = { matched: 0, withNotes: 0, newColors: 0, same: 0, cameras: 0, unmatched: [] };

  String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .forEach((raw) => {
      // a section label on its own: (Chorus), [Verse 2], Chorus:
      if (/^[([][^)\]]*[)\]]$/.test(raw) || /^(chorus|verse|bridge|intro|outro|hook|pre-?chorus)\b[^a-z]*$/i.test(raw)) return;
      const { lyric, note } = splitNote(raw);
      if (!lyric) return;
      // find the lyric: next exact match, else a close one, else anywhere
      let k = sung.findIndex((ln, idx) => idx >= ptr && normText(ln.text) === normText(lyric));
      if (k < 0) {
        let best = -1;
        let score = 0.6;
        sung.forEach((ln, idx) => {
          if (idx < ptr) return;
          const sc = similar(ln.text, lyric);
          if (sc > score) {
            score = sc;
            best = idx;
          }
        });
        k = best;
      }
      if (k < 0) k = sung.findIndex((ln) => normText(ln.text) === normText(lyric));
      if (k < 0) {
        report.unmatched.push(lyric);
        return;
      }
      ptr = k + 1;
      report.matched += 1;
      const ln = sung[k];
      const textKey = normText(ln.text);
      if (!note) return;
      report.withNotes += 1;

      let gid = "";
      if (SAME_RE.test(note) && lastGroupForText[textKey]) {
        gid = lastGroupForText[textKey];
        report.same += 1;
      } else {
        const key = normText(note);
        gid = byKey[key];
        if (!gid || !groups[gid]) {
          gid = newGroupId(groups);
          const color = nextColor(groups, note);
          const short = note.replace(/\s+/g, " ").trim();
          groups[gid] = {
            color,
            name: short.length > 28 ? `${short.slice(0, 26).trim()}…` : short,
            idea: note.trim(),
          };
          byKey[key] = gid;
          report.newColors += 1;
        }
      }
      lastGroupForText[textKey] = gid;
      next = assignLineToGroup({ ...next, groups }, ln.i, gid);

      const cam = cameraFromNote(SAME_RE.test(note) ? groups[gid]?.idea : note);
      if (cam !== null) {
        const cur = next.byLine[ln.i] ?? next.byLine[String(ln.i)];
        const base = cur && !cur.keep && cur.scene === "picture" ? cur : { cast: cur?.cast || {}, scene: "picture", picture: { url: "" } };
        next.byLine = { ...next.byLine, [ln.i]: { ...base, scene: "picture", picture: { ...(base.picture || {}), camera: cam } } };
        if (cam) report.cameras += 1;
      }
    });

  return { sb: { ...next, groups }, report };
};

export const StorylineBox = ({ sb, setSb, sung }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const start = () => {
    setText(storylineText(sb, sung));
    setResult(null);
    setOpen(true);
  };
  const apply = () => {
    const { sb: nextSb, report } = applyStoryline(sb, sung, text);
    setSb(nextSb);
    setResult(report);
  };
  return (
    <div className="lv-storyline">
      {!open ? (
        <button type="button" className="lb-btn lb-btn-soft lb-btn-sm" onClick={start}>
          📝 Write the storyline as text
        </button>
      ) : (
        <>
          <div className="lv-groups-head">
            <strong>📝 Storyline</strong>
            <span className="sa-muted">
              Every lyric line is here. After a line, write what its picture shows in (brackets). Lines with the same
              words in brackets get the same colour and share one picture. Write <code>(same as above)</code> on a repeat
              to reuse that line’s picture. Words like <code>zoom in to the side</code> or <code>zoom in under each bar</code>{" "}
              make the video zoom on the picture.
            </span>
          </div>
          <textarea
            className="lv-storyline-text"
            rows={14}
            value={text}
            spellCheck={false}
            onChange={(e) => setText(e.target.value)}
            placeholder={"Because on my bar graphs, we put in the data. (blue bar the tallest)\nOn my bar graph, it makes it all better. (yellow bar the tallest, blue the shortest)"}
          />
          <div className="lv-ed-row">
            <button type="button" className="lb-btn lb-btn-sm" onClick={apply} disabled={!text.trim()}>
              Fill in the colours and directions
            </button>
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={start} title="Throw away edits and write it out again from the storyboard">
              Start again from the storyboard
            </button>
            <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
          {result && (
            <div className={`sa-banner tone-${result.unmatched.length ? "amber" : "green"}`}>
              Matched {result.matched} line{result.matched === 1 ? "" : "s"}; {result.withNotes} with directions
              {result.newColors ? `, ${result.newColors} new colour${result.newColors === 1 ? "" : "s"}` : ""}
              {result.same ? `, ${result.same} “same as above”` : ""}
              {result.cameras ? `, ${result.cameras} camera zoom${result.cameras === 1 ? "" : "s"}` : ""}. Check the
              colours below, then press <b>✨ Make pictures</b>.
              {result.unmatched.length > 0 && (
                <>
                  {" "}
                  These didn’t match a lyric line and were skipped:{" "}
                  {result.unmatched.slice(0, 4).map((u) => `“${u.slice(0, 50)}”`).join(", ")}
                  {result.unmatched.length > 4 ? ` and ${result.unmatched.length - 4} more` : ""}. Directions for the
                  whole song go in “What every picture shows”.
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
